import React, { useState } from 'react';
import ProfileTestPreviewModal from './ProfileTestPreviewModal';

export const PackageReviewStep = ({
  formData,
  selectedComponents = [],
  allProfiles = [],
  isEditMode,
  saving,
  onBack,
  onSave
}) => {
  const [previewProfile, setPreviewProfile] = useState(null);

  const profiles = selectedComponents.filter(c => c.componentType === 'PROFILE');
  const directTests = selectedComponents.filter(c => c.componentType === 'TEST');

  const profileTestsCount = profiles.reduce((sum, p) => {
    const match = allProfiles.find(ap => ap.profileId === p.componentId);
    const count = match?.activeComponentCount ?? p.testCount ?? (Array.isArray(p.components) ? p.components.length : 0);
    return sum + (Number(count) || 0);
  }, 0);

  const totalEstimatedTests = profileTestsCount + directTests.length;

  return (
    <div className="pkg-review-container fade-in">
      <div className="pkg-card">
        <div className="pkg-card-header">
          <div className="pkg-card-header-icon">③</div>
          <div>
            <h3 className="pkg-card-title">Review & Finalize Health Package</h3>
            <p className="pkg-card-desc">
              Verify package identity, included profile groups, and standalone tests before saving to catalog.
            </p>
          </div>
        </div>

        <div className="pkg-card-body">
          {/* Section 1: Package Header Info */}
          <div className="pkg-review-hero">
            <div className="pkg-review-hero-main">
              <div className="pkg-card-top-row">
                <span className="pkg-badge-package">HEALTH PACKAGE</span>
                <span className={`pkg-review-status-pill ${formData.isActive ? 'active' : 'inactive'}`}>
                  {formData.isActive ? '● Active' : '○ Inactive'}
                </span>
              </div>
              <h2 className="pkg-review-package-name">{formData.packageName}</h2>
              <div className="pkg-review-meta-row">
                <span>Code: <strong>{formData.packageCode}</strong></span>
                {formData.displayOrder > 0 && <span>• Order: <strong>#{formData.displayOrder}</strong></span>}
              </div>
              {formData.description && (
                <p className="pkg-review-description">{formData.description}</p>
              )}
            </div>

            {/* Metric Overview Card */}
            <div className="pkg-review-stats-card">
              <span className="pkg-stats-heading">PACKAGE SCOPE</span>
              <div className="pkg-stats-row">
                <span>Profiles Included:</span>
                <strong>{profiles.length}</strong>
              </div>
              <div className="pkg-stats-row">
                <span>Direct Tests:</span>
                <strong>{directTests.length}</strong>
              </div>
              <div className="pkg-stats-divider" />
              <div className="pkg-stats-row total">
                <span>Total Parameters / Tests:</span>
                <strong className="pkg-total-highlight">{totalEstimatedTests}</strong>
              </div>
            </div>
          </div>

          {/* Section 2: Included Profiles */}
          <div className="pkg-review-section">
            <div className="pkg-review-section-header">
              <div className="pkg-review-section-title">
                <span className="pkg-section-icon">📋</span>
                <h4>Clinical Profiles ({profiles.length})</h4>
              </div>
              <span className="pkg-section-sub">
                Reusable groups containing {profileTestsCount} individual test parameters
              </span>
            </div>

            {profiles.length === 0 ? (
              <div className="pkg-review-empty-box">
                No profiles included in this package.
              </div>
            ) : (
              <div className="pkg-review-items-grid">
                {profiles.map((prof, idx) => {
                  const match = allProfiles.find(ap => ap.profileId === prof.componentId);
                  const count = match?.activeComponentCount ?? prof.testCount ?? (Array.isArray(prof.components) ? prof.components.length : 0);

                  return (
                    <div key={prof.componentId} className="pkg-review-item-card profile">
                      <div className="pkg-review-item-num">#{idx + 1}</div>
                      <div className="pkg-review-item-body">
                        <div className="pkg-card-top-row">
                          <span className="pkg-badge-profile">PROFILE</span>
                          <span className="pkg-test-count-badge">
                            <strong>{count}</strong> Tests
                          </span>
                        </div>
                        <h5 className="pkg-review-item-name">{prof.name}</h5>
                        <span className="pkg-review-item-code">
                          Code: {prof.code || '—'} {prof.departmentName ? `• ${prof.departmentName}` : ''}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="pkg-btn-secondary-xs"
                        onClick={() => setPreviewProfile(match || prof)}
                        title="View tests inside this profile"
                      >
                        👁️ View Tests
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 3: Direct Individual Tests */}
          <div className="pkg-review-section">
            <div className="pkg-review-section-header">
              <div className="pkg-review-section-title">
                <span className="pkg-section-icon">🧪</span>
                <h4>Direct Individual Tests ({directTests.length})</h4>
              </div>
              <span className="pkg-section-sub">
                Single investigations added directly to this package
              </span>
            </div>

            {directTests.length === 0 ? (
              <div className="pkg-review-empty-box">
                No standalone tests added directly.
              </div>
            ) : (
              <div className="pkg-review-items-grid">
                {directTests.map((test, idx) => (
                  <div key={test.componentId} className="pkg-review-item-card test">
                    <div className="pkg-review-item-num">#{idx + 1}</div>
                    <div className="pkg-review-item-body">
                      <div className="pkg-card-top-row">
                        <span className="pkg-badge-test">DIRECT TEST</span>
                      </div>
                      <h5 className="pkg-review-item-name">{test.name}</h5>
                      <span className="pkg-review-item-code">
                        Code: {test.code || '—'} • {test.departmentName || 'General Pathology'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Footer */}
        <div className="pkg-card-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onBack}
            disabled={saving}
          >
            ← Edit Components
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving}
            id="pkg-submit-save-btn"
          >
            {saving ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                <span className="pkg-spinner-sm" /> Saving Package...
              </span>
            ) : isEditMode ? (
              '💾 Update Health Package'
            ) : (
              '✓ Create Health Package'
            )}
          </button>
        </div>
      </div>

      {previewProfile && (
        <ProfileTestPreviewModal
          profile={previewProfile}
          onClose={() => setPreviewProfile(null)}
        />
      )}
    </div>
  );
};

export default PackageReviewStep;
